/**
 * BlogPostPage
 * 
 * Individual blog post page with MDX rendering.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHelmet from '../../components/SEOHelmet';
import { BlogConfig } from '../BlogConfig';
import { loadPostBySlug, loadAllPosts } from '../utils/postLoader';
import MarkdownRenderer from '../components/MarkdownRenderer';
import BottomNav from '../components/BottomNav';
import Config from '../../Config';
import { useLanguage } from '../../i18n/LanguageContext';

// Social Icons
const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z" />
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231z" />
  </svg>
);

const BlogPostPage = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  const loadPost = useCallback(async () => {
    try {
      setLoading(true);
      const localeMap = { en: 'en-US', pt: 'pt-BR', fr: 'fr-FR' };
      const currentLocale = localeMap[language];

      const loadedPost = await loadPostBySlug(slug, currentLocale);
      if (!loadedPost) {
        setError(t('blog.noPostsFound'));
        return;
      }
      setPost(loadedPost);

      const allPosts = await loadAllPosts(currentLocale);
      const related = allPosts
        .filter(p => p.category === loadedPost.category && p.slug !== loadedPost.slug)
        .slice(0, 3);
      setRelatedPosts(related);
    } catch (err) {
      setError(t('blog.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [slug, language, t]);

  useEffect(() => {
    loadPost();
    window.scrollTo(0, 0);
  }, [loadPost]);

  // Scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      const progressBar = document.getElementById('reading-progress');
      if (progressBar) progressBar.style.width = scrolled + "%";
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-20">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{error || t('blog.noPostsFound')}</h2>
          <Link to="/blog" className="text-blue-600 hover:text-blue-800 underline">← {t('blog.backToBlog')}</Link>
        </div>
      </div>
    );
  }

  const categoryConfig = BlogConfig.categories.find(cat => cat.id === post.category) || { label: post.category, color: '#6b7280' };
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/blog/${post.slug}` : '';

  return (
    <>
      <SEOHelmet
        title={post.title}
        description={post.description}
        pathname={`/blog/${post.slug}`}
        type="article"
        article={post}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Reading Progress Bar */}
        <div className="fixed top-0 left-0 w-full h-1 z-50">
          <div id="reading-progress" className="h-full bg-blue-600 transition-all duration-150" style={{ width: '0%' }} />
        </div>

        {/* Compact Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              {/* Back to Blog */}
              <Link to="/blog" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">{t('nav.blog')}</span>
              </Link>

              {/* Author */}
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white text-xs font-bold">
                  {Config.initials}
                </div>
                <span className="text-sm font-medium text-gray-900 hidden sm:block">{Config.name}</span>
              </Link>
            </div>
          </div>
        </header>

        {/* Article Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
            {/* Category */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link
                to={`/blog?category=${post.category}`}
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{ backgroundColor: `${categoryConfig.color}15`, color: categoryConfig.color }}
              >
                {t(`blog.categories.${post.category}`) || categoryConfig.label}
              </Link>
              {post.featured && <span className="text-amber-500">★ {t('blog.featured')}</span>}
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="font-medium text-gray-700">{post.author}</span>
              <span>·</span>
              <span>{post.formattedDate}</span>
              <span>·</span>
              <span>{post.readingTime} {t('blog.minuteRead')}</span>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {post.thumbnail && (
          <div className="max-w-4xl mx-auto px-4 -mt-6">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={post.thumbnail} alt={post.title} className="w-full h-auto" />
            </div>
          </div>
        )}

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-6 sm:p-10">
            <div className="prose prose-lg max-w-none text-gray-700">
              <MarkdownRenderer content={post.content} />
            </div>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map(tag => (
                    <span key={tag} className="text-sm text-gray-500">#{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Share Section */}
          <div className="mt-6 flex items-center justify-between bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Share:</span>
              <div className="flex gap-2">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-900 hover:text-white text-gray-600 transition-colors"
                >
                  <XIcon />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-full bg-gray-100 hover:bg-blue-600 hover:text-white text-gray-600 transition-colors"
                >
                  <LinkedInIcon />
                </a>
                <button
                  onClick={() => { navigator.clipboard.writeText(shareUrl); alert('Link copied!'); }}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                  title="Copy link"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Author mini card */}
            <Link to="/" className="hidden sm:flex items-center gap-3 hover:bg-gray-50 p-2 rounded-lg transition-colors">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white font-bold text-sm">
                {Config.initials}
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{Config.name}</div>
                <div className="text-xs text-gray-500">{t('blog.viewAll')}</div>
              </div>
            </Link>
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 pb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('blog.relatedArticles')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedPosts.map(relatedPost => (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 border border-gray-100"
                >
                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 text-sm">
                    {relatedPost.title}
                  </h3>
                  <p className="text-xs text-gray-500 mt-2">{relatedPost.formattedDate} · {relatedPost.readingTime} {t('blog.minuteRead')}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <BottomNav />
      </div>
    </>
  );
};

export default BlogPostPage;
